import type {
  ColDef,
  ColGroupDef,
  ColumnState,
  GridApi,
  GridState,
} from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'

import type {
  ExternalFilters,
  GridViewPayload,
  GridViewPayloadColumn,
  GridViewPayloadColumnSizing,
  GridViewPayloadSizing,
  GridViewPayloadSort,
} from '../types'
import { defaultIgnoreColumn } from '../persistence/useColumnStatePersistence'

const GRID_VIEW_PAYLOAD_VERSION = '2'
const COMPATIBLE_GRID_VIEW_PAYLOAD_VERSIONS = new Set(['1', '2'])
const COLUMN_WIDTH_TOLERANCE_PX = 2

type ColumnDefItem<Row> = ColDef<Row> | ColGroupDef<Row>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isColDef = <Row>(
  columnDef: ColumnDefItem<Row>,
): columnDef is ColDef<Row> => !('children' in columnDef)

const getColumnDefId = <Row>(columnDef: ColDef<Row>): string | null => {
  if (typeof columnDef.colId === 'string' && columnDef.colId.length > 0) {
    return columnDef.colId
  }
  if (typeof columnDef.field === 'string' && columnDef.field.length > 0) {
    return columnDef.field
  }
  return null
}

const sortByColId = <T extends { colId: string }>(left: T, right: T) =>
  left.colId.localeCompare(right.colId)

const normalizeExternalFilters = (
  filters: ExternalFilters | undefined,
  contextualExternalFilterFields: readonly string[] | undefined,
): ExternalFilters => {
  const blocked = new Set(contextualExternalFilterFields ?? [])
  return Object.entries(filters ?? {})
    .filter(
      ([field, values]) =>
        !blocked.has(field) && Array.isArray(values) && values.length > 0,
    )
    .sort(([left], [right]) => left.localeCompare(right))
    .reduce<ExternalFilters>((acc, [field, values]) => {
      acc[field] = [...values]
      return acc
    }, {})
}

const normalizeColumnState = ({
  columns,
  ignoreColumn = defaultIgnoreColumn,
}: {
  columns: ColumnState[]
  ignoreColumn?: (colId: string) => boolean
}): GridViewPayloadColumn[] =>
  columns.flatMap((column) => {
    if (typeof column.colId !== 'string') return []
    if (ignoreColumn(column.colId)) return []

    return [
      {
        colId: column.colId,
        ...(typeof column.hide === 'boolean' ? { hidden: column.hide } : {}),
        ...(column.pinned === 'left' || column.pinned === 'right'
          ? { pinned: column.pinned }
          : {}),
      },
    ]
  })

const normalizeColumnSizingState = ({
  columns,
  ignoreColumn = defaultIgnoreColumn,
}: {
  columns: ColumnState[]
  ignoreColumn?: (colId: string) => boolean
}): GridViewPayloadSizing | undefined => {
  const sizingColumns = columns.flatMap((column) => {
    if (typeof column.colId !== 'string') return []
    if (ignoreColumn(column.colId)) return []

    const width =
      typeof column.width === 'number' && Number.isFinite(column.width)
        ? Math.round(column.width)
        : undefined
    const flex =
      typeof column.flex === 'number' && Number.isFinite(column.flex)
        ? column.flex
        : undefined
    if (typeof width !== 'number' && typeof flex !== 'number') return []

    return [
      {
        colId: column.colId,
        ...(typeof width === 'number' ? { width } : {}),
        ...(typeof flex === 'number' ? { flex } : {}),
      },
    ]
  })

  return sizingColumns.length > 0 ? { columns: sizingColumns } : undefined
}

const normalizeSortState = ({
  columns,
  ignoreColumn = defaultIgnoreColumn,
}: {
  columns: ColumnState[]
  ignoreColumn?: (colId: string) => boolean
}): GridViewPayloadSort[] =>
  columns
    .flatMap((column) => {
      if (typeof column.colId !== 'string') return []
      if (ignoreColumn(column.colId)) return []
      if (column.sort !== 'asc' && column.sort !== 'desc') return []

      return [
        {
          colId: column.colId,
          sort: column.sort,
          ...(typeof column.sortIndex === 'number'
            ? { sortIndex: column.sortIndex }
            : {}),
        },
      ]
    })
    .sort((left, right) => {
      if (left.sortIndex == null && right.sortIndex == null) {
        return left.colId.localeCompare(right.colId)
      }
      if (left.sortIndex == null) return 1
      if (right.sortIndex == null) return -1
      return left.sortIndex - right.sortIndex
    })

const normalizeFilterModel = (
  filterModel: unknown,
  contextualExternalFilterFields?: readonly string[],
): Record<string, unknown> => {
  if (!isRecord(filterModel)) return {}

  const blocked = new Set(contextualExternalFilterFields ?? [])
  return Object.entries(filterModel)
    .filter(([field]) => !blocked.has(field))
    .sort(([left], [right]) => left.localeCompare(right))
    .reduce<Record<string, unknown>>((acc, [field, value]) => {
      acc[field] = value
      return acc
    }, {})
}

const isMalformedPayloadColumn = (value: unknown): boolean => {
  if (!isRecord(value)) return true
  if (typeof value.colId !== 'string' || value.colId.length === 0) return true
  if ('hidden' in value && typeof value.hidden !== 'boolean') return true
  if (
    'pinned' in value &&
    value.pinned !== 'left' &&
    value.pinned !== 'right'
  ) {
    return true
  }
  return false
}

const isMalformedPayloadSort = (value: unknown): boolean => {
  if (!isRecord(value)) return true
  if (typeof value.colId !== 'string' || value.colId.length === 0) return true
  if (value.sort !== 'asc' && value.sort !== 'desc') return true
  if (
    'sortIndex' in value &&
    (typeof value.sortIndex !== 'number' || !Number.isFinite(value.sortIndex))
  ) {
    return true
  }
  return false
}

const isMalformedPayloadExternalFilters = (value: unknown): boolean => {
  if (!isRecord(value)) return true
  return Object.values(value).some(
    (values) =>
      !Array.isArray(values) ||
      values.some(
        (item) => typeof item !== 'string' && typeof item !== 'number',
      ),
  )
}

const isMalformedPayloadColumnSizing = (value: unknown): boolean => {
  if (!isRecord(value) || !Array.isArray(value.columns)) return true

  const seen = new Set<string>()
  return value.columns.some((column) => {
    if (!isRecord(column)) return true
    if (typeof column.colId !== 'string' || column.colId.length === 0) {
      return true
    }
    if (seen.has(column.colId)) return true
    seen.add(column.colId)

    if (
      'width' in column &&
      (typeof column.width !== 'number' || !Number.isFinite(column.width))
    ) {
      return true
    }
    if (
      'flex' in column &&
      (typeof column.flex !== 'number' || !Number.isFinite(column.flex))
    ) {
      return true
    }

    return typeof column.width !== 'number' && typeof column.flex !== 'number'
  })
}

const flattenSystemColumns = <Row>(
  columnDefs: AgGridReactProps<Row>['columnDefs'],
  ignoreColumn = defaultIgnoreColumn,
): GridViewPayloadColumn[] => {
  const defs = Array.isArray(columnDefs) ? columnDefs : []

  const flattenColumnDef = (
    columnDef: ColumnDefItem<Row>,
  ): GridViewPayloadColumn[] => {
    if (!isColDef(columnDef)) {
      return columnDef.children.flatMap(flattenColumnDef)
    }

    const colId = getColumnDefId(columnDef)
    if (!colId || ignoreColumn(colId)) return []

    return [
      {
        colId,
        ...(columnDef.hide === true ? { hidden: true } : {}),
        ...(columnDef.pinned === 'left' || columnDef.pinned === 'right'
          ? { pinned: columnDef.pinned }
          : {}),
      },
    ]
  }

  return defs.flatMap(flattenColumnDef)
}

const normalizePinned = (value: unknown): 'left' | 'right' | null =>
  value === 'left' || value === 'right' ? value : null

const normalizeSort = (value: unknown): 'asc' | 'desc' | null =>
  value === 'asc' || value === 'desc' ? value : null

const normalizeSortIndex = (value: unknown): number | null =>
  typeof value === 'number' ? value : null

const stableSerialize = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

const normalizePayloadColumns = (
  columns: GridViewPayloadColumn[] | undefined,
): GridViewPayloadColumn[] =>
  (Array.isArray(columns) ? columns : []).flatMap((column) => {
    if (!column || typeof column.colId !== 'string') return []

    return [
      {
        colId: column.colId,
        ...(typeof column.hidden === 'boolean'
          ? { hidden: column.hidden }
          : {}),
        ...(column.pinned === 'left' || column.pinned === 'right'
          ? { pinned: column.pinned }
          : {}),
      },
    ]
  })

const normalizePayloadColumnSizing = (
  sizing: GridViewPayloadSizing | undefined,
): GridViewPayloadSizing | undefined => {
  const seen = new Set<string>()
  const columns = (Array.isArray(sizing?.columns) ? sizing.columns : [])
    .flatMap<GridViewPayloadColumnSizing>((column) => {
      if (!column || typeof column.colId !== 'string') return []
      if (seen.has(column.colId)) return []
      seen.add(column.colId)

      const width =
        typeof column.width === 'number' && Number.isFinite(column.width)
          ? Math.round(column.width)
          : undefined
      const flex =
        typeof column.flex === 'number' && Number.isFinite(column.flex)
          ? column.flex
          : undefined
      if (typeof width !== 'number' && typeof flex !== 'number') return []

      return [
        {
          colId: column.colId,
          ...(typeof width === 'number' ? { width } : {}),
          ...(typeof flex === 'number' ? { flex } : {}),
        },
      ]
    })
    .sort((left, right) => left.colId.localeCompare(right.colId))

  return columns.length > 0 ? { columns } : undefined
}

export const getGridViewPayloadVersion = () => GRID_VIEW_PAYLOAD_VERSION

export const isCompatibleGridViewPayloadVersion = (
  payloadVersion: string | null | undefined,
) =>
  typeof payloadVersion === 'string' &&
  COMPATIBLE_GRID_VIEW_PAYLOAD_VERSIONS.has(payloadVersion)

export const normalizeGridViewPayload = (
  payload: GridViewPayload,
  {
    contextualExternalFilterFields,
  }: { contextualExternalFilterFields?: readonly string[] } = {},
): GridViewPayload => {
  const columnSizing = normalizePayloadColumnSizing(payload.columnSizing)

  return {
    columns: normalizePayloadColumns(payload.columns),
    sort: [...payload.sort],
    filterModel: normalizeFilterModel(
      payload.filterModel,
      contextualExternalFilterFields,
    ),
    externalFilters: normalizeExternalFilters(
      payload.externalFilters,
      contextualExternalFilterFields,
    ),
    ...(columnSizing ? { columnSizing } : {}),
  }
}

export const serializeGridViewPayload = (
  payload: GridViewPayload,
  options?: { contextualExternalFilterFields?: readonly string[] },
): string => JSON.stringify(normalizeGridViewPayload(payload, options))

export const stableSerializeGridViewPayload = (
  payload: GridViewPayload,
  options?: { contextualExternalFilterFields?: readonly string[] },
): string => stableSerialize(normalizeGridViewPayload(payload, options))

const stripColumnSizing = (payload: GridViewPayload): GridViewPayload => {
  const { columnSizing: _columnSizing, ...rest } = payload
  return rest
}

const areColumnSizingEqual = (
  left: GridViewPayloadSizing | undefined,
  right: GridViewPayloadSizing | undefined,
) => {
  const leftSizing = normalizePayloadColumnSizing(left)
  const rightSizing = normalizePayloadColumnSizing(right)
  const leftColumns = leftSizing?.columns ?? []
  const rightColumns = rightSizing?.columns ?? []

  if (leftColumns.length !== rightColumns.length) return false

  return leftColumns.every((leftColumn, index) => {
    const rightColumn = rightColumns[index]
    if (!rightColumn || leftColumn.colId !== rightColumn.colId) return false
    if (leftColumn.flex !== rightColumn.flex) return false

    if (
      typeof leftColumn.width === 'number' ||
      typeof rightColumn.width === 'number'
    ) {
      if (
        typeof leftColumn.width !== 'number' ||
        typeof rightColumn.width !== 'number'
      ) {
        return false
      }

      return (
        Math.abs(leftColumn.width - rightColumn.width) <=
        COLUMN_WIDTH_TOLERANCE_PX
      )
    }

    return true
  })
}

export const areGridViewPayloadsEqual = (
  left: GridViewPayload,
  right: GridViewPayload,
  options?: { contextualExternalFilterFields?: readonly string[] },
): boolean => {
  const normalizedLeft = normalizeGridViewPayload(left, options)
  const normalizedRight = normalizeGridViewPayload(right, options)

  return (
    stableSerialize(stripColumnSizing(normalizedLeft)) ===
      stableSerialize(stripColumnSizing(normalizedRight)) &&
    areColumnSizingEqual(
      normalizedLeft.columnSizing,
      normalizedRight.columnSizing,
    )
  )
}

export const parseGridViewPayload = (
  raw: string | null | undefined,
  options?: {
    contextualExternalFilterFields?: readonly string[]
    rejectMalformed?: boolean
  },
): GridViewPayload | null => {
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<GridViewPayload>
    if (!parsed || typeof parsed !== 'object') return null

    const rawColumns: unknown = parsed.columns
    const rawSort: unknown = parsed.sort
    const rawExternalFilters: unknown = parsed.externalFilters
    const rejectMalformed = options?.rejectMalformed === true
    if (
      rejectMalformed &&
      (!Array.isArray(rawColumns) || rawColumns.some(isMalformedPayloadColumn))
    ) {
      return null
    }
    if (
      rejectMalformed &&
      (!Array.isArray(rawSort) || rawSort.some(isMalformedPayloadSort))
    ) {
      return null
    }
    if (rejectMalformed && !isRecord(parsed.filterModel)) {
      return null
    }
    if (
      rejectMalformed &&
      isMalformedPayloadExternalFilters(rawExternalFilters)
    ) {
      return null
    }
    if (
      rejectMalformed &&
      parsed.columnSizing !== undefined &&
      isMalformedPayloadColumnSizing(parsed.columnSizing)
    ) {
      return null
    }

    const columns = Array.isArray(parsed.columns)
      ? parsed.columns.flatMap((column) => {
          if (!isRecord(column) || typeof column.colId !== 'string') return []
          return [
            {
              colId: column.colId,
              ...(typeof column.hidden === 'boolean'
                ? { hidden: column.hidden }
                : {}),
              ...(column.pinned === 'left' || column.pinned === 'right'
                ? { pinned: column.pinned }
                : {}),
            },
          ]
        })
      : []

    const sort = Array.isArray(parsed.sort)
      ? parsed.sort
          .flatMap((entry) => {
            if (!isRecord(entry) || typeof entry.colId !== 'string') return []
            if (entry.sort !== 'asc' && entry.sort !== 'desc') return []
            return [
              {
                colId: entry.colId,
                sort: entry.sort,
                ...(typeof entry.sortIndex === 'number'
                  ? { sortIndex: entry.sortIndex }
                  : {}),
              },
            ]
          })
          .sort((left, right) => {
            if (left.sortIndex == null && right.sortIndex == null) {
              return sortByColId(left, right)
            }
            if (left.sortIndex == null) return 1
            if (right.sortIndex == null) return -1
            return left.sortIndex - right.sortIndex
          })
      : []

    const externalFilters = normalizeExternalFilters(
      isRecord(rawExternalFilters)
        ? (rawExternalFilters as ExternalFilters)
        : {},
      options?.contextualExternalFilterFields,
    )
    const columnSizing = normalizePayloadColumnSizing(
      isRecord(parsed.columnSizing)
        ? (parsed.columnSizing as GridViewPayloadSizing)
        : undefined,
    )

    return {
      columns,
      sort,
      filterModel: normalizeFilterModel(
        parsed.filterModel,
        options?.contextualExternalFilterFields,
      ),
      externalFilters,
      ...(columnSizing ? { columnSizing } : {}),
    }
  } catch {
    return null
  }
}

/**
 * Reconcile a payload saved against an older column contract with the current
 * system payload.
 *
 * Column order/visibility/pinning and valid sorts are retained. Filtered
 * payloads fail closed because the persisted payload does not record filter
 * types, so a schema change cannot prove that an old filter remains valid.
 * New columns inherit the current system defaults, obsolete sizing is
 * discarded, and ambiguous references fail closed so a view can fall back to
 * a known safe state.
 */
export const reconcileGridViewPayload = ({
  payload,
  systemPayload,
  contextualExternalFilterFields,
  externalFilterFields,
}: {
  payload: GridViewPayload
  systemPayload: GridViewPayload
  contextualExternalFilterFields?: readonly string[]
  externalFilterFields?: readonly string[]
}): GridViewPayload | null => {
  if (payload.columns.length === 0 || systemPayload.columns.length === 0) {
    return null
  }

  const systemColumnsById = new Map(
    systemPayload.columns.map((column) => [column.colId, column]),
  )
  if (systemColumnsById.size !== systemPayload.columns.length) return null

  const seenColumnIds = new Set<string>()
  const retainedColumns: GridViewPayloadColumn[] = []
  for (const column of payload.columns) {
    if (seenColumnIds.has(column.colId)) return null
    seenColumnIds.add(column.colId)

    const systemColumn = systemColumnsById.get(column.colId)
    if (!systemColumn) continue

    retainedColumns.push(column)
  }
  if (retainedColumns.length === 0) return null

  const columns = [
    ...retainedColumns,
    ...systemPayload.columns.filter(
      (column) => !seenColumnIds.has(column.colId),
    ),
  ]

  const seenSortIds = new Set<string>()
  for (const sort of payload.sort) {
    // A saved sort may only target a column that was part of the saved
    // payload. A newly introduced system column has no trustworthy persisted
    // sort semantics until the user saves the view again.
    if (!seenColumnIds.has(sort.colId) || !systemColumnsById.has(sort.colId)) {
      return null
    }
    if (seenSortIds.has(sort.colId)) return null
    seenSortIds.add(sort.colId)
  }

  const filterEntries = Object.entries(payload.filterModel)
  if (filterEntries.length > 0) return null

  if (externalFilterFields) {
    const allowedExternalFilterFields = new Set([
      ...externalFilterFields,
      ...(contextualExternalFilterFields ?? []),
    ])
    const hasObsoleteExternalFilter = Object.entries(
      payload.externalFilters,
    ).some(
      ([field, values]) =>
        values.length > 0 && !allowedExternalFilterFields.has(field),
    )
    if (hasObsoleteExternalFilter) return null
  }

  const columnSizing = payload.columnSizing
    ? {
        columns: payload.columnSizing.columns.filter(
          (column) =>
            seenColumnIds.has(column.colId) &&
            systemColumnsById.has(column.colId),
        ),
      }
    : undefined

  return normalizeGridViewPayload(
    {
      columns,
      sort: payload.sort,
      filterModel: {},
      externalFilters: payload.externalFilters,
      ...(columnSizing && columnSizing.columns.length > 0
        ? { columnSizing }
        : {}),
    },
    { contextualExternalFilterFields },
  )
}

export const buildSystemGridViewPayload = <Row>({
  columnDefs,
  defaultExternalFilters,
  contextualExternalFilterFields,
  ignoreColumn = defaultIgnoreColumn,
}: {
  columnDefs: AgGridReactProps<Row>['columnDefs']
  defaultExternalFilters?: ExternalFilters
  contextualExternalFilterFields?: readonly string[]
  ignoreColumn?: (colId: string) => boolean
}): GridViewPayload => ({
  columns: flattenSystemColumns(columnDefs, ignoreColumn),
  sort: [],
  filterModel: {},
  externalFilters: normalizeExternalFilters(
    defaultExternalFilters,
    contextualExternalFilterFields,
  ),
})

export const captureGridViewPayload = <Row>({
  api,
  externalFilters,
  filterModel,
  contextualExternalFilterFields,
  ignoreColumn = defaultIgnoreColumn,
  includeColumnSizing = false,
}: {
  api: GridApi<Row>
  externalFilters: ExternalFilters
  filterModel?: Record<string, unknown> | null
  contextualExternalFilterFields?: readonly string[]
  ignoreColumn?: (colId: string) => boolean
  includeColumnSizing?: boolean
}): GridViewPayload => {
  const columnState = api.getColumnState()
  const columnSizing = includeColumnSizing
    ? normalizeColumnSizingState({ columns: columnState, ignoreColumn })
    : undefined

  return {
    columns: normalizeColumnState({ columns: columnState, ignoreColumn }),
    sort: normalizeSortState({ columns: columnState, ignoreColumn }),
    filterModel: normalizeFilterModel(
      filterModel !== undefined ? filterModel : api.getFilterModel(),
      contextualExternalFilterFields,
    ),
    externalFilters: normalizeExternalFilters(
      externalFilters,
      contextualExternalFilterFields,
    ),
    ...(columnSizing ? { columnSizing } : {}),
  }
}

export const validateGridViewPayloadForApi = <Row>({
  api,
  payload,
  ignoreColumn = defaultIgnoreColumn,
}: {
  api: GridApi<Row>
  payload: GridViewPayload
  ignoreColumn?: (colId: string) => boolean
}): boolean => {
  const eligibleColumnIds =
    api
      .getColumns()
      ?.map((column) => column.getId())
      .filter((colId) => !ignoreColumn(colId)) ?? []
  if (eligibleColumnIds.length === 0) return false

  const expectedColumnIds = new Set(eligibleColumnIds)
  const seenPayloadColumnIds = new Set<string>()
  for (const column of payload.columns) {
    if (!expectedColumnIds.has(column.colId)) return false
    if (seenPayloadColumnIds.has(column.colId)) return false
    seenPayloadColumnIds.add(column.colId)
  }
  if (seenPayloadColumnIds.size !== expectedColumnIds.size) return false

  const seenSizingColumnIds = new Set<string>()
  for (const column of payload.columnSizing?.columns ?? []) {
    if (!expectedColumnIds.has(column.colId)) return false
    if (seenSizingColumnIds.has(column.colId)) return false
    seenSizingColumnIds.add(column.colId)
  }

  return true
}

export const buildGridViewInitialState = (
  payload: GridViewPayload,
): GridState => {
  const leftColIds: string[] = []
  const rightColIds: string[] = []

  payload.columns.forEach((column) => {
    if (column.pinned === 'left') {
      leftColIds.push(column.colId)
    }
    if (column.pinned === 'right') {
      rightColIds.push(column.colId)
    }
  })

  return {
    partialColumnState: true,
    columnOrder: {
      orderedColIds: payload.columns.map((column) => column.colId),
    },
    columnVisibility: {
      hiddenColIds: payload.columns
        .filter((column) => column.hidden === true)
        .map((column) => column.colId),
    },
    columnPinning: {
      leftColIds,
      rightColIds,
    },
    ...(payload.columnSizing
      ? {
          columnSizing: {
            columnSizingModel: payload.columnSizing.columns.map((column) => ({
              colId: column.colId,
              ...(typeof column.width === 'number'
                ? { width: column.width }
                : {}),
              ...(typeof column.flex === 'number' ? { flex: column.flex } : {}),
            })),
          },
        }
      : {}),
    sort: {
      sortModel: payload.sort.map((column) => ({
        colId: column.colId,
        sort: column.sort,
      })),
    },
    filter: {
      filterModel: payload.filterModel,
    },
  }
}

export const projectGridViewColumnDefs = <Row>({
  columnDefs,
  payload,
}: {
  columnDefs: AgGridReactProps<Row>['columnDefs']
  payload: GridViewPayload
}): AgGridReactProps<Row>['columnDefs'] => {
  const defs = Array.isArray(columnDefs) ? columnDefs : []
  if (defs.length === 0) return columnDefs

  const hasColumnGroups = defs.some((columnDef) => !isColDef(columnDef))
  const sortById = new Map(payload.sort.map((entry) => [entry.colId, entry]))
  const sizingById = new Map(
    (payload.columnSizing?.columns ?? []).map((entry) => [entry.colId, entry]),
  )

  const projectColumn = (
    columnDef: ColDef<Row>,
    viewColumn: GridViewPayloadColumn | undefined,
    forceHidden: boolean,
  ): ColDef<Row> => {
    const colId = getColumnDefId(columnDef)
    if (!colId) return columnDef

    const sort = sortById.get(colId)
    const sizing = sizingById.get(colId)
    const nextHide = forceHidden || viewColumn?.hidden === true
    const nextPinned = viewColumn?.pinned ?? null
    const nextSort = sort?.sort ?? null
    const nextSortIndex = sort?.sortIndex ?? null
    const nextWidth =
      sizing && typeof sizing.width === 'number' ? sizing.width : undefined
    const nextFlex =
      sizing && typeof sizing.flex === 'number' ? sizing.flex : undefined

    const hideIsEquivalent = columnDef.hide === true ? nextHide : !nextHide
    const pinnedIsEquivalent = normalizePinned(columnDef.pinned) === nextPinned
    const sortIsEquivalent = normalizeSort(columnDef.sort) === nextSort
    const sortIndexIsEquivalent =
      normalizeSortIndex(columnDef.sortIndex) === nextSortIndex
    const widthIsEquivalent =
      nextWidth === undefined || columnDef.width === nextWidth
    const flexIsEquivalent =
      nextFlex === undefined || columnDef.flex === nextFlex

    if (
      hideIsEquivalent &&
      pinnedIsEquivalent &&
      sortIsEquivalent &&
      sortIndexIsEquivalent &&
      widthIsEquivalent &&
      flexIsEquivalent
    ) {
      return columnDef
    }

    return {
      ...columnDef,
      hide: nextHide,
      pinned: nextPinned,
      sort: nextSort,
      sortIndex: nextSortIndex,
      ...(nextWidth === undefined ? {} : { width: nextWidth }),
      ...(nextFlex === undefined ? {} : { flex: nextFlex }),
    }
  }

  const payloadColumnById = new Map(
    payload.columns.map((column) => [column.colId, column]),
  )

  if (hasColumnGroups) {
    const projectGroupChildren = (
      items: ColumnDefItem<Row>[],
    ): ColumnDefItem<Row>[] => {
      let didChange = false
      const projectedItems = items.map((item) => {
        if (isColDef(item)) {
          const colId = getColumnDefId(item)
          const projected = projectColumn(
            item,
            colId ? payloadColumnById.get(colId) : undefined,
            colId ? !payloadColumnById.has(colId) : false,
          )
          if (projected !== item) didChange = true
          return projected
        }

        const projectedChildren = projectGroupChildren(item.children)
        if (projectedChildren === item.children) return item
        didChange = true
        return {
          ...item,
          children: projectedChildren,
        }
      })

      return didChange ? projectedItems : items
    }

    const projectedGroups = projectGroupChildren(defs)
    return projectedGroups === defs ? columnDefs : projectedGroups
  }

  const defsById = new Map(
    defs.flatMap((columnDef) => {
      if (!isColDef(columnDef)) return []
      const colId = getColumnDefId(columnDef)
      return colId ? [[colId, columnDef] as const] : []
    }),
  )
  const projectedIds = new Set<string>()

  const orderedColumns = payload.columns.flatMap((viewColumn) => {
    const columnDef = defsById.get(viewColumn.colId)
    if (!columnDef) return []
    projectedIds.add(viewColumn.colId)
    return [projectColumn(columnDef, viewColumn, false)]
  })

  const remainingColumns = defs.flatMap((columnDef) => {
    if (!isColDef(columnDef)) return []
    const colId = getColumnDefId(columnDef)
    if (colId && projectedIds.has(colId)) return []
    return [projectColumn(columnDef, undefined, Boolean(colId))]
  })

  const projectedColumns = [...orderedColumns, ...remainingColumns]
  const hasProjectionChanged =
    projectedColumns.length !== defs.length ||
    projectedColumns.some((columnDef, index) => columnDef !== defs[index])

  return hasProjectionChanged ? projectedColumns : columnDefs
}

export const applyGridViewPayload = <Row>({
  api,
  payload,
}: {
  api: GridApi<Row>
  payload: GridViewPayload
}) => {
  api.applyColumnState({
    state: payload.columns.map((column) => ({
      colId: column.colId,
      hide: column.hidden === true,
      pinned: column.pinned ?? null,
    })),
    applyOrder: true,
    defaultState: {
      hide: true,
      pinned: null,
    },
  })

  if ((payload.columnSizing?.columns.length ?? 0) > 0) {
    api.applyColumnState({
      state: payload.columnSizing!.columns.map((column) => ({
        colId: column.colId,
        ...(typeof column.width === 'number' ? { width: column.width } : {}),
        ...(typeof column.flex === 'number' ? { flex: column.flex } : {}),
      })),
    })
  }

  api.applyColumnState({
    state: payload.sort.map((column) => ({
      colId: column.colId,
      sort: column.sort,
      ...(typeof column.sortIndex === 'number'
        ? { sortIndex: column.sortIndex }
        : {}),
    })),
    defaultState: {
      sort: null,
      sortIndex: null,
    },
  })

  api.setFilterModel(payload.filterModel)
  api.onFilterChanged()
}
